import React, { useState, useEffect, useCallback } from 'react';
import { styles, colors } from '../../styles/globalStyles';

export const GerenciadorConfiguracoes = ({ createApi, isMobile = false }) => {
  const [configuracoes, setConfiguracoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notificacao, setNotificacao] = useState({ tipo: '', mensagem: '' });
  
  const [novaConfig, setNovaConfig] = useState({
    nome: '',
    descricao: '',
    intervalo_minutos: 60,
    vagas_por_horario: 1,
    horarios: {
      0: { ativo: false, inicio: '09:00', fim: '12:00' },
      1: { ativo: true, inicio: '07:00', fim: '19:00' },
      2: { ativo: true, inicio: '07:00', fim: '19:00' },
      3: { ativo: true, inicio: '07:00', fim: '19:00' },
      4: { ativo: true, inicio: '07:00', fim: '19:00' },
      5: { ativo: true, inicio: '07:00', fim: '19:00' },
      6: { ativo: true, inicio: '09:00', fim: '13:00' }
    },
    pausas: []
  });

  const [configEmEdicao, setConfigEmEdicao] = useState(null);

  const diasSemana = [
    { id: 0, nome: 'Domingo', abreviacao: 'Dom' },
    { id: 1, nome: 'Segunda-feira', abreviacao: 'Seg' },
    { id: 2, nome: 'Terça-feira', abreviacao: 'Ter' },
    { id: 3, nome: 'Quarta-feira', abreviacao: 'Qua' },
    { id: 4, nome: 'Quinta-feira', abreviacao: 'Qui' },
    { id: 5, nome: 'Sexta-feira', abreviacao: 'Sex' },
    { id: 6, nome: 'Sábado', abreviacao: 'Sáb' }
  ];

  const showNotificacao = useCallback((tipo, mensagem) => {
    setNotificacao({ tipo, mensagem });
    setTimeout(() => setNotificacao({ tipo: '', mensagem: '' }), 5000);
  }, []);

  const carregarConfiguracoes = useCallback(async () => {
    setLoading(true);
    try {
      const api = createApi();
      const response = await api.get('/admin/configuracoes-semana');
      setConfiguracoes(response.data);
    } catch (err) {
      const mensagemErro = err.response?.data?.error || 'Erro ao carregar configurações';
      setError(mensagemErro);
      showNotificacao('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  }, [createApi, showNotificacao]);

  const iniciarEdicao = (config) => {
    setConfigEmEdicao(config);
    setNovaConfig({
      nome: config.nome,
      descricao: config.descricao || '',
      intervalo_minutos: config.intervalo_minutos,
      vagas_por_horario: config.vagas_por_horario || 1,
      horarios: config.horarios || {
        0: { ativo: false, inicio: '09:00', fim: '12:00' },
        1: { ativo: true, inicio: '07:00', fim: '19:00' },
        2: { ativo: true, inicio: '07:00', fim: '19:00' },
        3: { ativo: true, inicio: '07:00', fim: '19:00' },
        4: { ativo: true, inicio: '07:00', fim: '19:00' },
        5: { ativo: true, inicio: '07:00', fim: '19:00' },
        6: { ativo: true, inicio: '09:00', fim: '13:00' }
      },
      pausas: Array.isArray(config.pausas) ? config.pausas : []
    });
  };

  const cancelarEdicao = () => {
    setConfigEmEdicao(null);
    setNovaConfig({
      nome: '',
      descricao: '',
      intervalo_minutos: 60,
      vagas_por_horario: 1,
      horarios: {
        0: { ativo: false, inicio: '09:00', fim: '12:00' },
        1: { ativo: true, inicio: '07:00', fim: '19:00' },
        2: { ativo: true, inicio: '07:00', fim: '19:00' },
        3: { ativo: true, inicio: '07:00', fim: '19:00' },
        4: { ativo: true, inicio: '07:00', fim: '19:00' },
        5: { ativo: true, inicio: '07:00', fim: '19:00' },
        6: { ativo: true, inicio: '09:00', fim: '13:00' }
      },
      pausas: []
    });
  };

  const adicionarPausa = () => {
    setNovaConfig(prev => ({
      ...prev,
      pausas: [...(prev.pausas || []), { inicio: '12:00', fim: '14:00', motivo: 'Almoço' }]
    }));
  };

  const atualizarPausa = (idx, campo, valor) => {
    setNovaConfig(prev => {
      const pausas = [...(prev.pausas || [])];
      pausas[idx] = { ...pausas[idx], [campo]: valor };
      return { ...prev, pausas };
    });
  };

  const removerPausa = (idx) => {
    setNovaConfig(prev => ({
      ...prev,
      pausas: (prev.pausas || []).filter((_, i) => i !== idx)
    }));
  };

  const salvarConfiguracao = async () => {
    if (!novaConfig.nome.trim()) {
      const mensagemErro = 'Nome da configuração é obrigatório';
      setError(mensagemErro);
      showNotificacao('error', mensagemErro);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const api = createApi();
      
      if (configEmEdicao) {
        // Editar configuração existente
        const response = await api.put(`/admin/configuracoes-semana/${configEmEdicao.id}`, novaConfig);
        setConfiguracoes(prev => 
          prev.map(c => c.id === configEmEdicao.id ? response.data.configuracao : c)
        );
        showNotificacao('success', 'Configuração atualizada com sucesso!');
      } else {
        // Criar nova configuração
        const response = await api.post('/admin/configuracoes-semana', novaConfig);
        setConfiguracoes(prev => [...prev, response.data.configuracao]);
        showNotificacao('success', 'Configuração criada com sucesso!');
      }
      
      cancelarEdicao();
    } catch (err) {
      const mensagemErro = err.response?.data?.error || 
                          (configEmEdicao ? 'Erro ao atualizar configuração' : 'Erro ao criar configuração');
      setError(mensagemErro);
      showNotificacao('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const ativarConfiguracao = async (id) => {
    if (!window.confirm('Ativar esta configuração como padrão?')) return;
    
    try {
      const api = createApi();
      await api.post(`/admin/configuracoes-semana/${id}/ativar`);
      carregarConfiguracoes();
      showNotificacao('success', 'Configuração ativada com sucesso!');
    } catch (err) {
      const mensagemErro = err.response?.data?.error || 'Erro ao ativar configuração';
      setError(mensagemErro);
      showNotificacao('error', mensagemErro);
    }
  };

  const excluirConfiguracao = async (id) => {
    if (!window.confirm('Excluir esta configuração?')) return;
    
    try {
      const api = createApi();
      const response = await api.delete(`/admin/configuracoes-semana/${id}`);
      
      // Atualiza a lista localmente
      setConfiguracoes(prev => prev.filter(c => c.id !== id));
      
      showNotificacao('success', response.data.message || 'Configuração excluída com sucesso!');
    } catch (err) {
      console.error('❌ Erro ao excluir configuração:', err);
      
      const mensagemErro = err.response?.data?.error || 
                          err.response?.data?.message || 
                          'Erro ao excluir configuração';
      
      showNotificacao('error', mensagemErro);
      
      // Tenta recarregar as configurações em caso de erro
      carregarConfiguracoes();
    }
  };

  const atualizarHorario = (dia, campo, valor) => {
    setNovaConfig(prev => ({
      ...prev,
      horarios: {
        ...prev.horarios,
        [dia]: {
          ...prev.horarios[dia],
          [campo]: valor
        }
      }
    }));
  };

  const formatarHorarioParaExibicao = (horario) => {
    const { ativo, inicio, fim } = horario;
    if (!ativo) return 'Fechado';
    return `${inicio} - ${fim}`;
  };

  useEffect(() => {
    carregarConfiguracoes();
  }, [carregarConfiguracoes]);

  return (
    <div style={{ 
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
      boxSizing: 'border-box'
    }}>
      <h3 style={{ 
        marginBottom: '1.5rem', 
        color: colors.dark,
        fontSize: isMobile ? '1.2rem' : '1.5rem'
      }}>⚙ Configurações da Semana</h3>
      
      {/* Notificação */}
      {notificacao.mensagem && (
        <div style={{
          ...styles.alert,
          ...(notificacao.tipo === 'success' ? styles.alertSuccess : styles.alertError),
          marginBottom: '1rem',
          fontSize: isMobile ? '0.9rem' : '1rem',
          padding: isMobile ? '0.75rem' : '1rem'
        }}>
          {notificacao.mensagem}
        </div>
      )}

      {/* Erro */}
      {error && (
        <div style={{ 
          ...styles.alert, 
          ...styles.alertError, 
          marginBottom: '1rem',
          fontSize: isMobile ? '0.9rem' : '1rem',
          padding: isMobile ? '0.75rem' : '1rem'
        }}>
          {error}
        </div>
      )}

      {/* Formulário para nova/editar configuração */}
      <div style={{ 
        ...styles.card, 
        marginBottom: isMobile ? '1.5rem' : '2rem',
        padding: isMobile ? '1rem' : '1.5rem'
      }}>
        <h4 style={{ 
          marginBottom: '1rem', 
          color: colors.dark,
          fontSize: isMobile ? '1.1rem' : '1.25rem'
        }}>
          {configEmEdicao ? '✏️ Editar Configuração' : '➕ Nova Configuração'}
          {configEmEdicao && (
            <span style={{
              ...styles.badge,
              ...styles.badgeInfo,
              marginLeft: '10px',
              fontSize: isMobile ? '11px' : '12px'
            }}>
              Editando: {configEmEdicao.nome}
            </span>
          )}
        </h4>
        
        <div style={styles.formGroup}>
          <label style={{
            ...styles.formLabel,
            fontSize: isMobile ? '0.95rem' : '1rem'
          }}>Nome da Configuração *</label>
          <input
            type="text"
            value={novaConfig.nome}
            onChange={(e) => setNovaConfig({...novaConfig, nome: e.target.value})}
            style={{
              ...styles.formInput,
              fontSize: isMobile ? '0.95rem' : '1rem',
              padding: isMobile ? '0.75rem' : '0.75rem 1rem'
            }}
            placeholder="Ex: Horário Padrão"
          />
        </div>

        <div style={styles.formGroup}>
          <label style={{
            ...styles.formLabel,
            fontSize: isMobile ? '0.95rem' : '1rem'
          }}>Descrição</label>
          <textarea
            value={novaConfig.descricao}
            onChange={(e) => setNovaConfig({...novaConfig, descricao: e.target.value})}
            style={{ 
              ...styles.formInput, 
              minHeight: '80px',
              fontSize: isMobile ? '0.95rem' : '1rem',
              padding: isMobile ? '0.75rem' : '0.75rem 1rem'
            }}
            placeholder="Descrição da configuração..."
          />
        </div>

        <div style={styles.formGroup}>
          <label style={{
            ...styles.formLabel,
            fontSize: isMobile ? '0.95rem' : '1rem'
          }}>Intervalo entre vagas (minutos)</label>
          <select
            value={novaConfig.intervalo_minutos}
            onChange={(e) => setNovaConfig({...novaConfig, intervalo_minutos: parseInt(e.target.value)})}
            style={{
              ...styles.formSelect,
              fontSize: isMobile ? '0.95rem' : '1rem',
              padding: isMobile ? '0.75rem' : '0.75rem 1rem'
            }}
          >
            <option value="30">30 minutos</option>
            <option value="45">45 minutos</option>
            <option value="60">60 minutos</option>
            <option value="90">90 minutos</option>
            <option value="120">120 minutos</option>
          </select>
        </div>

        {/* NOVO CAMPO: Vagas por horário */}
        <div style={styles.formGroup}>
          <label style={{
            ...styles.formLabel,
            fontSize: isMobile ? '0.95rem' : '1rem'
          }}>Vagas por horário (1-6) *</label>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            marginTop: '0.5rem'
          }}>
            {[1, 2, 3, 4, 5, 6].map(num => (
              <button
                key={num}
                type="button"
                onClick={() => setNovaConfig({...novaConfig, vagas_por_horario: num})}
                style={{
                  padding: isMobile ? '0.5rem 0.75rem' : '0.625rem 1rem',
                  borderRadius: '8px',
                  border: `2px solid ${novaConfig.vagas_por_horario === num ? colors.primary : colors.grayLight}`,
                  backgroundColor: novaConfig.vagas_por_horario === num ? colors.primary : 'white',
                  color: novaConfig.vagas_por_horario === num ? 'white' : colors.dark,
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: isMobile ? '0.875rem' : '0.9rem',
                  minWidth: isMobile ? '40px' : '50px',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px'
                }}
              >
                {num}
                {num === 1 ? ' vaga' : ' vagas'}
              </button>
            ))}
          </div>
          <p style={{
            marginTop: '0.5rem',
            fontSize: isMobile ? '0.8rem' : '0.85rem',
            color: colors.gray
          }}>
            Quantidade de vagas que serão criadas para cada horário definido
          </p>
        </div>

        <h5 style={{ 
          margin: '1.5rem 0 1rem', 
          color: colors.dark,
          fontSize: isMobile ? '1rem' : '1.1rem'
        }}>Horários por Dia</h5>
        
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: isMobile ? '0.75rem' : '1rem',
          maxWidth: '100%',
          overflowX: 'hidden'
        }}>
          {diasSemana.map(dia => (
            <div key={dia.id} style={{
              padding: isMobile ? '0.75rem' : '1rem',
              backgroundColor: colors.grayLight,
              borderRadius: '0.5rem',
              border: `1px solid ${novaConfig.horarios[dia.id].ativo ? colors.primary : '#e2e8f0'}`
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: isMobile ? '0.75rem' : '1rem', 
                marginBottom: isMobile ? '0.5rem' : '0.75rem'
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem', 
                  flex: 1,
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={novaConfig.horarios[dia.id].ativo}
                    onChange={(e) => atualizarHorario(dia.id, 'ativo', e.target.checked)}
                    style={{ 
                      width: isMobile ? '1rem' : '1rem', 
                      height: isMobile ? '1rem' : '1rem',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ 
                    fontWeight: '600',
                    fontSize: isMobile ? '0.95rem' : '1rem'
                  }}>{isMobile ? dia.abreviacao : dia.nome}</span>
                </label>
                <span style={{
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  color: colors.gray,
                  backgroundColor: novaConfig.horarios[dia.id].ativo ? '#d1fae5' : '#f1f5f9',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.25rem'
                }}>
                  {formatarHorarioParaExibicao(novaConfig.horarios[dia.id])}
                </span>
              </div>
              
              {novaConfig.horarios[dia.id].ativo && (
                <div style={{ 
                  display: 'flex', 
                  gap: isMobile ? '0.75rem' : '1rem', 
                  flexWrap: isMobile ? 'wrap' : 'nowrap'
                }}>
                  <div style={{ flex: 1, minWidth: isMobile ? '100%' : '150px' }}>
                    <label style={{ 
                      ...styles.formLabel, 
                      fontSize: isMobile ? '0.85rem' : '0.875rem' 
                    }}>Início</label>
                    <input
                      type="time"
                      value={novaConfig.horarios[dia.id].inicio}
                      onChange={(e) => atualizarHorario(dia.id, 'inicio', e.target.value)}
                      style={{
                        ...styles.formInput,
                        fontSize: isMobile ? '0.95rem' : '1rem',
                        padding: isMobile ? '0.5rem' : '0.5rem 0.75rem',
                        width: '100%'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: isMobile ? '100%' : '150px' }}>
                    <label style={{ 
                      ...styles.formLabel, 
                      fontSize: isMobile ? '0.85rem' : '0.875rem' 
                    }}>Fim</label>
                    <input
                      type="time"
                      value={novaConfig.horarios[dia.id].fim}
                      onChange={(e) => atualizarHorario(dia.id, 'fim', e.target.value)}
                      style={{
                        ...styles.formInput,
                        fontSize: isMobile ? '0.95rem' : '1rem',
                        padding: isMobile ? '0.5rem' : '0.5rem 0.75rem',
                        width: '100%'
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ── Pausas / Intervalos ── */}
        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <label style={{ ...styles.formLabel, fontWeight: 700, fontSize: isMobile ? '0.9rem' : '0.95rem' }}>
              🔒 Intervalos travados (ex: almoço)
            </label>
            <button
              type="button"
              onClick={adicionarPausa}
              style={{
                padding: '0.35rem 0.8rem', borderRadius: '8px', border: 'none',
                background: 'var(--color-primary,#0f5a30)', color: 'white',
                fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer'
              }}
            >
              + Adicionar
            </button>
          </div>

          {(!novaConfig.pausas || novaConfig.pausas.length === 0) ? (
            <div style={{ padding: '0.75rem 1rem', background: '#f8fafc', borderRadius: '10px', border: '1px dashed #e2e8f0', fontSize: '0.82rem', color: '#94a3b8', textAlign: 'center' }}>
              Nenhum intervalo configurado — todas as vagas geradas serão livres
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {(novaConfig.pausas || []).map((pausa, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 0.8rem', background: '#fffbeb', borderRadius: '10px', border: '1px solid #fde68a', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                  <input
                    type="time"
                    value={pausa.inicio}
                    onChange={e => atualizarPausa(idx, 'inicio', e.target.value)}
                    style={{ padding: '0.4rem 0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.88rem', width: '110px' }}
                  />
                  <span style={{ fontSize: '0.8rem', color: '#92400e', fontWeight: 600 }}>até</span>
                  <input
                    type="time"
                    value={pausa.fim}
                    onChange={e => atualizarPausa(idx, 'fim', e.target.value)}
                    style={{ padding: '0.4rem 0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.88rem', width: '110px' }}
                  />
                  <input
                    type="text"
                    value={pausa.motivo}
                    onChange={e => atualizarPausa(idx, 'motivo', e.target.value)}
                    placeholder="Motivo (ex: Almoço)"
                    style={{ flex: 1, padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.85rem', minWidth: '100px' }}
                  />
                  <button
                    type="button"
                    onClick={() => removerPausa(idx)}
                    style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid #fca5a5', background: '#fff1f1', color: '#dc2626', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, flexShrink: 0 }}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <div style={{ fontSize: '0.75rem', color: '#92400e', marginTop: '2px' }}>
                ⚠️ As vagas nesses horários serão criadas automaticamente como <strong>travadas</strong> ao gerar a agenda
              </div>
            </div>
          )}
        </div>

        <div style={{
          marginTop: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '0.75rem' : '0'
        }}>
          {configEmEdicao && (
            <button
              onClick={cancelarEdicao}
              style={{
                ...styles.button,
                ...styles.buttonOutline,
                padding: isMobile ? '0.75rem' : '0.625rem 1.25rem',
                fontSize: isMobile ? '0.95rem' : '0.875rem',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              Cancelar Edição
            </button>
          )}
          <button
            onClick={salvarConfiguracao}
            disabled={loading || !novaConfig.nome.trim()}
            style={{
              ...styles.button,
              ...styles.buttonPrimary,
              opacity: loading || !novaConfig.nome.trim() ? 0.7 : 1,
              cursor: loading || !novaConfig.nome.trim() ? 'not-allowed' : 'pointer',
              marginLeft: isMobile ? '0' : 'auto',
              padding: isMobile ? '0.75rem' : '0.625rem 1.25rem',
              fontSize: isMobile ? '0.95rem' : '0.875rem',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            {loading ? 'Salvando...' : (configEmEdicao ? 'Atualizar Configuração' : 'Salvar Configuração')}
          </button>
        </div>
      </div>

      {/* Lista de configurações existentes */}
      <div style={{ 
        ...styles.card,
        padding: isMobile ? '1rem' : '1.5rem'
      }}>
        <h4 style={{ 
          marginBottom: '1rem', 
          color: colors.dark,
          fontSize: isMobile ? '1.1rem' : '1.25rem'
        }}>Configurações Salvas</h4>
        
        {loading && configuracoes.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: isMobile ? '2rem 1rem' : '2rem'
          }}>
            <div style={{
              ...styles.spinner,
              width: isMobile ? '2rem' : '3rem',
              height: isMobile ? '2rem' : '3rem'
            }}></div>
            <p style={{ 
              marginTop: '1rem',
              color: colors.gray,
              fontSize: isMobile ? '0.9rem' : '1rem'
            }}>Carregando configurações...</p>
          </div>
        ) : configuracoes.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: isMobile ? '2rem 1rem' : '2rem', 
            color: colors.gray,
            fontSize: isMobile ? '0.95rem' : '1rem'
          }}>
            Nenhuma configuração salva
          </div>
        ) : (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: isMobile ? '0.75rem' : '1rem'
          }}>
            {configuracoes.map(config => (
              <div key={config.id} style={{
                padding: isMobile ? '0.75rem' : '1rem',
                backgroundColor: colors.grayLight,
                borderRadius: '0.5rem',
                borderLeft: `4px solid ${config.ativa ? colors.success : colors.gray}`
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '0.5rem' : '0'
                }}>
                  <div style={{ flex: 1 }}>
                    <h5 style={{ 
                      margin: '0 0 0.5rem 0', 
                      color: colors.dark,
                      fontSize: isMobile ? '1rem' : '1.1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      flexWrap: 'wrap'
                    }}>
                      {config.nome} {config.ativa && (
                        <span style={{
                          ...styles.badge,
                          ...styles.badgeSuccess,
                          marginLeft: '0.5rem',
                          fontSize: isMobile ? '0.7rem' : '0.75rem'
                        }}>
                          Ativa
                        </span>
                      )}
                    </h5>
                    {config.descricao && (
                      <p style={{ 
                        margin: '0 0 0.5rem 0', 
                        color: colors.gray, 
                        fontSize: isMobile ? '0.85rem' : '0.875rem' 
                      }}>
                        {config.descricao}
                      </p>
                    )}
                    <div style={{ 
                      display: 'flex', 
                      gap: isMobile ? '0.75rem' : '1rem', 
                      fontSize: isMobile ? '0.8rem' : '0.875rem', 
                      color: colors.gray,
                      flexWrap: 'wrap'
                    }}>
                      <span>Intervalo: {config.intervalo_minutos} min</span>
                      <span>Vagas/horário: {config.vagas_por_horario || 1}</span>
                      <span>Dias ativos: {Object.values(config.horarios || {}).filter(h => h.ativo).length}</span>
                    </div>
                    {/* Resumo dos horários */}
                    <div style={{
                      marginTop: '0.75rem',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.5rem'
                    }}>
                      {diasSemana.map(dia => {
                        const horario = config.horarios?.[dia.id];
                        if (!horario?.ativo) return null;
                        return (
                          <span key={dia.id} style={{
                            backgroundColor: colors.white,
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: isMobile ? '0.75rem' : '0.8rem',
                            color: colors.primary,
                            border: `1px solid ${colors.primaryLight}`
                          }}>
                            {isMobile ? dia.abreviacao : dia.nome.substr(0, 3)}: {horario.inicio}-{horario.fim}
                          </span>
                        );
                      })}
                    </div>
                    {/* Pausas */}
                    {Array.isArray(config.pausas) && config.pausas.length > 0 && (
                      <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                        {config.pausas.map((p, i) => (
                          <span key={i} style={{
                            background: '#fffbeb', border: '1px solid #fde68a',
                            borderRadius: '0.25rem', padding: '0.2rem 0.5rem',
                            fontSize: '0.75rem', color: '#92400e'
                          }}>
                            🔒 {p.motivo || 'Pausa'}: {p.inicio}–{p.fim}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    gap: isMobile ? '0.5rem' : '0.5rem',
                    flexDirection: isMobile ? 'row' : 'row',
                    width: isMobile ? '100%' : 'auto',
                    marginTop: isMobile ? '0.5rem' : '0'
                  }}>
                    {!config.ativa && (
                      <button
                        onClick={() => ativarConfiguracao(config.id)}
                        style={{
                          ...styles.button,
                          ...styles.buttonSuccess,
                          ...styles.buttonSmall,
                          padding: isMobile ? '0.5rem 0.75rem' : '0.375rem 0.75rem',
                          fontSize: isMobile ? '0.85rem' : '0.75rem'
                        }}
                      >
                        Ativar
                      </button>
                    )}
                    <button
                      onClick={() => iniciarEdicao(config)}
                      style={{
                        ...styles.button,
                        ...styles.buttonOutline,
                        ...styles.buttonSmall,
                        padding: isMobile ? '0.5rem 0.75rem' : '0.375rem 0.75rem',
                        fontSize: isMobile ? '0.85rem' : '0.75rem'
                      }}
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => excluirConfiguracao(config.id)}
                      style={{
                        ...styles.button,
                        ...styles.buttonDanger,
                        ...styles.buttonSmall,
                        padding: isMobile ? '0.5rem 0.75rem' : '0.375rem 0.75rem',
                        fontSize: isMobile ? '0.85rem' : '0.75rem'
                      }}
                    >
                      Excluir
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
